<template>
  <div>
    <!-- Attachment pills — rendered above the input when uploads exist. -->
    <div v-if="uploads && uploads.length > 0" class="upload-pills">
      <div
        v-for="upload in uploads"
        :key="upload.id"
        class="upload-pill"
        :class="`upload-pill--${upload.status}`"
      >
        <v-icon
          :color="upload.status === 'error' ? 'red darken-1' : 'grey darken-1'"
          size="14"
          :class="upload.status === 'uploading' ? 'spin-icon' : ''"
        >
          {{ uploadIcon(upload) }}
        </v-icon>
        <span class="pill-name">{{ upload.filename }}</span>
        <span v-if="upload.status === 'attached'" class="pill-meta">
          · {{ formatSize(upload.size_bytes) }}
        </span>
        <span
          v-else-if="upload.status === 'error'"
          class="pill-error"
          :title="upload.error"
        >
          · {{ upload.error }}
        </span>
        <v-btn
          icon
          x-small
          class="pill-remove"
          aria-label="Remove attachment"
          @click="$emit('remove-upload', upload.id)"
        >
          <v-icon size="14">mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <div class="chat-input" :class="{ 'chat-input--focused': focused }">
      <!-- Paperclip / file picker -->
      <v-btn
        icon
        small
        class="attach-btn"
        :disabled="disabled"
        aria-label="Attach a document"
        @click="openFilePicker"
      >
        <v-icon size="20">mdi-paperclip</v-icon>
      </v-btn>
      <input
        ref="fileInputRef"
        type="file"
        :accept="ACCEPTED_MIME_AND_EXTS"
        multiple
        style="display: none;"
        @change="onFilesPicked"
      />

      <textarea
        ref="textareaRef"
        :value="displayedDraft"
        :placeholder="voiceListening ? 'Listening…' : placeholder"
        :disabled="disabled"
        rows="1"
        class="chat-textarea"
        @input="onTextareaInput"
        @keydown="onKeydown"
        @focus="focused = true"
        @blur="focused = false"
      />

      <!--
        Mic button — only shown when the browser exposes SpeechRecognition.
        Click toggles dictation; interim words stream into the input live,
        final utterances commit on pause. Pulsing red ring when listening
        so the user never forgets the mic is hot.
      -->
      <button
        v-if="voiceSupported"
        type="button"
        class="mic-btn"
        :class="{ 'mic-btn--listening': voiceListening }"
        :disabled="disabled"
        :aria-label="voiceListening ? 'Stop dictation' : 'Start dictation'"
        :title="voiceError ? `Mic error: ${voiceError}` : voiceListening ? 'Stop' : 'Dictate a message'"
        @click="toggleVoice"
      >
        <v-icon :color="voiceListening ? 'white' : 'grey darken-1'" size="18">
          {{ voiceListening ? 'mdi-stop' : 'mdi-microphone' }}
        </v-icon>
      </button>

      <button
        class="send-btn"
        :class="{ 'send-btn--active': canSend }"
        :disabled="!canSend"
        aria-label="Send message"
        @click="submit"
      >
        <v-icon :color="canSend ? 'white' : 'grey'" size="18">mdi-arrow-up</v-icon>
      </button>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, nextTick, ref, watch } from 'vue';
import { createVoiceInput } from '@rm-ui/agent/voiceInput';

// File picker hint — server still validates mime type + extension.
const ACCEPTED_MIME_AND_EXTS =
  '.pdf,.docx,.doc,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown';

export default defineComponent({
  name: 'ChatInput',
  props: {
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: '' },
    /** Uploads in progress or attached. Rendered as pills above the input. */
    uploads: { type: Array, default: () => [] },
  },
  emits: ['submit', 'upload-files', 'remove-upload'],
  setup(props, { emit }) {
    const draft = ref('');
    const textareaRef = ref(null);
    const fileInputRef = ref(null);
    const focused = ref(false);

    // Voice dictation via browser SpeechRecognition. The composable
    // owns recognizer lifecycle; this component only binds the
    // reactive state to UI + merges finalised utterances into `draft`.
    const {
      isSupported: voiceSupported,
      isListening: voiceListening,
      interimTranscript: voiceInterim,
      error: voiceError,
      start: startVoice,
      stop: stopVoice,
    } = createVoiceInput(ref);

    // What the textarea renders. Normally just `draft`; while
    // listening we append the live interim transcript so the user
    // sees words appearing as they speak. Interim text is NOT
    // committed to `draft` — only finalised utterances are, on pause.
    const displayedDraft = computed(() => {
      if (voiceListening.value && voiceInterim.value) {
        const prefix = draft.value
          ? draft.value + (draft.value.endsWith(' ') ? '' : ' ')
          : '';
        return prefix + voiceInterim.value;
      }
      return draft.value;
    });

    function onTextareaInput(e) {
      // Typing during dictation stops the mic — otherwise the user's
      // typed edits would fight the interim transcript overwrite.
      if (voiceListening.value) stopVoice();
      draft.value = e.target.value;
      autoResize();
    }

    function toggleVoice() {
      if (voiceListening.value) {
        stopVoice();
        return;
      }
      startVoice((finalText) => {
        const trimmed = (finalText || '').trim();
        if (!trimmed) return;
        draft.value = draft.value
          ? `${draft.value.replace(/\s+$/, '')} ${trimmed}`
          : trimmed;
        nextTick(autoResize);
      });
    }

    // Send is allowed if there's text OR at least one successfully attached file.
    // Lets users send an attachment alone ("here's a doc — what do you make of it?").
    const hasAttachedFile = computed(
      () => (props.uploads || []).some((u) => u.status === 'attached'),
    );
    const canSend = computed(
      () => !props.disabled && (draft.value.trim().length > 0 || hasAttachedFile.value),
    );

    function submit() {
      if (!canSend.value) return;
      // Stop any live dictation so in-flight interim doesn't leak
      // into the next turn.
      if (voiceListening.value) stopVoice();
      const value = draft.value.trim();
      draft.value = '';
      emit('submit', value);
      nextTick(autoResize);
    }

    function onKeydown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    }

    function autoResize() {
      const el = textareaRef.value;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 160) + 'px';
    }

    function openFilePicker() {
      if (fileInputRef.value) fileInputRef.value.click();
    }

    function onFilesPicked(e) {
      const input = e.target;
      const files = input.files ? Array.from(input.files) : [];
      if (files.length > 0) emit('upload-files', files);
      // Clear so picking the same file twice in a row re-fires change.
      input.value = '';
    }

    watch(
      () => props.disabled,
      (isDisabled) => {
        if (!isDisabled) {
          nextTick(() => textareaRef.value && textareaRef.value.focus());
        }
      },
    );

    // ── Upload pill visuals ──

    function uploadIcon(u) {
      if (u.status === 'uploading') return 'mdi-progress-clock';
      if (u.status === 'error') return 'mdi-alert-circle';
      return 'mdi-file-document-outline';
    }

    function formatSize(bytes) {
      if (bytes < 1024) return `${bytes}B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    }

    return {
      draft,
      textareaRef,
      fileInputRef,
      focused,
      canSend,
      submit,
      onKeydown,
      autoResize,
      openFilePicker,
      onFilesPicked,
      uploadIcon,
      formatSize,
      ACCEPTED_MIME_AND_EXTS,
      voiceSupported,
      voiceListening,
      voiceError,
      displayedDraft,
      onTextareaInput,
      toggleVoice,
    };
  },
});
</script>

<style scoped>
/* Attachment pills */
.upload-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}
.upload-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 12px;
  border: 1px solid;
  background: white;
  max-width: 280px;
}
.upload-pill--uploading {
  background: rgb(249 250 251);
  border-color: rgb(229 231 235);
  color: rgb(55 65 81);
}
.upload-pill--attached {
  background: rgb(238 242 255);
  border-color: rgb(199 210 254);
  color: rgb(67 56 202);
}
.upload-pill--error {
  background: rgb(254 242 242);
  border-color: rgb(254 202 202);
  color: rgb(185 28 28);
}
.pill-name {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}
.pill-meta { color: rgb(107 114 128); font-size: 11px; }
.pill-error {
  color: rgb(185 28 28);
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}
.pill-remove { margin-left: 2px !important; }
.spin-icon { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Input row */
.chat-input {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  border: 1px solid rgb(209 213 219);
  background: white;
  border-radius: 16px;
  padding: 6px 8px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
/* Focus treatment from the DS AtlasChat composer: border saturates to
   Atlas indigo with a soft 3px halo, instead of a thin 1px ring. */
.chat-input--focused {
  border-color: rgb(79 70 229);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.25);
}
.attach-btn {
  flex-shrink: 0;
  margin: 0 !important;
}
.chat-textarea {
  flex: 1;
  resize: none;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: rgb(17 24 39);
  max-height: 160px;
  padding: 6px 0;
  font-family: inherit;
}
.chat-textarea::placeholder { color: rgb(156 163 175); }
.send-btn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgb(229 231 235);
  cursor: not-allowed;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}
.send-btn--active {
  background: rgb(79 70 229);
  cursor: pointer;
}
.send-btn--active:hover { background: rgb(67 56 202); }

/* Mic button — shown only when browser exposes SpeechRecognition. */
.mic-btn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.15s ease;
}
.mic-btn:hover { background: rgb(243 244 246); }
.mic-btn:disabled { cursor: not-allowed; opacity: 0.5; }
.mic-btn--listening {
  background: rgb(239 68 68);
  animation: mic-ring-pulse 1.4s ease-in-out infinite;
}
.mic-btn--listening:hover { background: rgb(220 38 38); }
@keyframes mic-ring-pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.55);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(239, 68, 68, 0);
  }
}
</style>
